import { motion } from "framer-motion";
import { useState } from "react";
import ShowPassword from "../../assets/svg/ShowPassword.svg";
import HidePassword from "../../assets/svg/HidePassword.svg";

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1,
            delayChildren: 0.2
        }
    }
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
    hover: { scale: 1.02 },
    tap: { scale: 0.98 }
};

const StepOneForm = ({ formData, handleChange, nextStep, formErrors, generalError }) => {
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmationPassword, setConfirmationPassword] = useState(false);

    const togglePasswordVisibility = () => setShowPassword(prev => !prev);
    const toggleConfirmationPasswordVisibility = () => setConfirmationPassword(prev => !prev);

    const baseInputClasses = "w-full h-input md:h-input-md sl:h-input-md ll:h-input-lg px-4 md:px-6 sl:px-6 ll:px-8 bg-accent rounded-lg focus:outline-none text-base md:text-md sl:text-lg ll:text-xl placeholder:text-secondary/70 border";
    const getInputClasses = (hasError) =>
        `${baseInputClasses} ${hasError ? "border-red-500 focus:ring-2 focus:ring-red-500" : "border-transparent focus:ring-2 focus:ring-primary"}`;

    return (
        <motion.form
            className="space-y-4 md:space-y-6 sl:space-y-4 ll:space-y-7 pt-8 ll:pt-0"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
        >
            {generalError && (
                <motion.div
                    variants={itemVariants}
                    className="p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-600"
                >
                    {generalError}
                </motion.div>
            )}

            <motion.div
                className="grid grid-cols-1 md:grid-cols-1 ll:grid-cols-2 space-y-4 md:space-y-6 sl:space-y-4 ll:gap-4 ll:space-y-0"
                variants={itemVariants}
            >
                <motion.div variants={itemVariants}>
                    <input
                        type="text"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        placeholder="First Name*"
                        className={getInputClasses(!!formErrors.first_name)}
                        aria-invalid={!!formErrors.first_name}
                    />
                    {formErrors.first_name && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.first_name}</p>
                    )}
                </motion.div>

                <motion.div variants={itemVariants}>
                    <input
                        type="text"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        placeholder="Last Name*"
                        className={getInputClasses(!!formErrors.last_name)}
                        aria-invalid={!!formErrors.last_name}
                    />
                    {formErrors.last_name && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.last_name}</p>
                    )}
                </motion.div>
            </motion.div>

            <motion.div variants={itemVariants}>
                <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Email*"
                    className={getInputClasses(!!formErrors.email)}
                    aria-invalid={!!formErrors.email}
                />
                {formErrors.email && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>
                )}
            </motion.div>

            <motion.div variants={itemVariants}>
                <input
                    type="tel"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleChange}
                    placeholder="Mobile phone*"
                    className={getInputClasses(!!formErrors.phone_number)}
                    aria-invalid={!!formErrors.phone_number}
                />
                {formErrors.phone_number && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.phone_number}</p>
                )}
            </motion.div>

            <motion.div
                className="flex flex-col space-y-3 md:space-y-0 md:flex-row md:space-x-6 sl:space-x-8 ll:space-x-10"
                variants={itemVariants}
            >
                <div className="flex items-center space-x-2">
                    <input
                        id="male"
                        type="radio"
                        value="male"
                        name="gender"
                        className="w-4 h-4 md:w-5 md:h-5 sl:w-5 sl:h-5  text-primary cursor-pointer"
                        checked={formData.gender === "male"}
                        onChange={handleChange}
                    />
                    <label htmlFor="male" className="text-base md:text-md sl:text-lg ll:text-xl text-secondary cursor-pointer">
                        Male
                    </label>
                </div>
                <div className="flex items-center space-x-2">
                    <input
                        id="female"
                        type="radio"
                        value="female"
                        name="gender"
                        className="w-4 h-4 md:w-5 md:h-5 sl:w-5 sl:h-5 text-primary cursor-pointer"
                        checked={formData.gender === "female"}
                        onChange={handleChange}
                    />
                    <label htmlFor="female" className="text-base md:text-md sl:text-lg ll:text-xl text-secondary cursor-pointer">
                        Female
                    </label>
                </div>
            </motion.div>
            {formErrors.gender && (
                <p className="text-sm text-red-600">{formErrors.gender}</p>
            )}

            <motion.div variants={itemVariants}>
                <div className="relative">
                    <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="Password*"
                        className={`${getInputClasses(!!formErrors.password)} pr-12 md:pr-14 sl:pr-16`}
                        aria-invalid={!!formErrors.password}
                    />
                    <motion.button
                        type="button"
                        onClick={togglePasswordVisibility}
                        className="absolute right-4 md:right-6 sl:right-8 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-accent-dark"
                    >
                        <img
                            className="w-5 h-5 md:w-6 md:h-6 sl:w-6 sl:h-6 ll:w-7 ll:h-7"
                            src={showPassword ? HidePassword : ShowPassword}
                            alt="Toggle password visibility"
                        />
                    </motion.button>
                </div>
                {formErrors.password && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.password}</p>
                )}
            </motion.div>

            <motion.div variants={itemVariants}>
                <div className="relative">
                    <input
                        type={showConfirmationPassword ? "text" : "password"}
                        name="password_confirmation"
                        value={formData.password_confirmation}
                        onChange={handleChange}
                        placeholder="Confirm Password*"
                        className={`${getInputClasses(!!formErrors.password_confirmation)} pr-12 md:pr-14 sl:pr-16`}
                        aria-invalid={!!formErrors.password_confirmation}
                    />
                    <motion.button
                        type="button"
                        onClick={toggleConfirmationPasswordVisibility}
                        className="absolute right-4 md:right-6 sl:right-8 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-accent-dark"
                    >
                        <img
                            className="w-5 h-5 md:w-6 md:h-6 sl:w-6 sl:h-6 ll:w-7 ll:h-7"
                            src={showConfirmationPassword ? HidePassword : ShowPassword}
                            alt="Toggle password visibility"
                        />
                    </motion.button>
                </div>
                {formErrors.password_confirmation && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.password_confirmation}</p>
                )}
            </motion.div>

            <motion.div variants={itemVariants}>
                <motion.button
                    type="button"
                    onClick={nextStep}
                    className="w-full h-input md:h-input-md sl:h-input-md ll:h-input-lg bg-primary text-white text-md md:text-md sl:text-lg ll:text-xl rounded-lg hover:bg-primary-hover transition-colors font-medium"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                >
                    Next
                </motion.button>
            </motion.div>
        </motion.form>
    );
};

export default StepOneForm;